import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame, extend } from '@react-three/fiber';
import type { Sphere as _Sphere } from '@react-three/drei';
import * as THREE from 'three';

// Extend R3F with Line
extend({ Line_: THREE.Line });
import { TrajectoryData, ShotAnimation } from '../types';

interface BallProps {
  trajectory: TrajectoryData;
  animation: ShotAnimation;
  trailLength?: number;
}

export const Ball: React.FC<BallProps> = ({
  trajectory,
  animation,
  trailLength = 20
}) => {
  const ballRef = useRef<THREE.Mesh>(null);
  const trailRef = useRef<THREE.Line>(null);
  const trailPoints = useRef<THREE.Vector3[]>([]);

  // Calculate ball position based on animation time
  const ballPosition = useMemo(() => {
    if (!animation.isPlaying && animation.currentTime === 0) {
      // Pre-animation: ball at bowler's hand
      return new THREE.Vector3(0, 2, 15);
    }
    
    if (animation.currentPhase === 'preImpact') {
      // Ball approaching from bowler
      const progress = animation.currentTime / animation.phases.preImpact.duration;
      const startPos = new THREE.Vector3(0, 2, 15);
      const endPos = new THREE.Vector3(0, 1.5, 0); // Impact position
      
      return startPos.lerp(endPos, progress);
    }
    
    if (animation.currentPhase === 'impact') {
      // Ball at impact position
      return new THREE.Vector3(0, 1.5, 0);
    }
    
    if (animation.currentPhase === 'postImpact') {
      // Ball following trajectory
      const postImpactTime = animation.currentTime - animation.phases.postImpact.startTime;
      const flightProgress = postImpactTime / (animation.phases.postImpact.duration * animation.speed);
      
      const pointIndex = Math.floor(trajectory.points.length * flightProgress);
      if (pointIndex >= trajectory.points.length - 1) {
        return trajectory.points[trajectory.points.length - 1].position;
      }
      
      const currentPoint = trajectory.points[pointIndex];
      const nextPoint = trajectory.points[pointIndex + 1];
      
      if (!currentPoint || !nextPoint) return new THREE.Vector3(0, 1.5, 0);
      
      const localProgress = (trajectory.points.length * flightProgress) - pointIndex;
      return currentPoint.position.clone().lerp(nextPoint.position, localProgress);
    }
    
    return new THREE.Vector3(0, 1.5, 0);
  }, [animation, trajectory]);

  // Update ball position and rotation
  useFrame(() => {
    if (ballRef.current) {
      ballRef.current.position.copy(ballPosition);
      
      // Rotate ball based on spin
      if (animation.isPlaying && animation.currentPhase === 'postImpact') {
        const spinRate = 2000; // RPM
        const rotationSpeed = (spinRate / 60) * 2 * Math.PI; // rad/s
        ballRef.current.rotation.x += rotationSpeed * 0.016; // 60fps
      }
    }
    
    // Update trail
    if (animation.isPlaying && animation.currentPhase === 'postImpact') {
      trailPoints.current.push(ballPosition.clone());
      
      // Limit trail length
      if (trailPoints.current.length > trailLength) {
        trailPoints.current.shift();
      }
      
      // Update trail geometry
      if (trailRef.current && trailPoints.current.length > 1) {
        const geometry = new THREE.BufferGeometry().setFromPoints(trailPoints.current);
        trailRef.current.geometry = geometry;
      }
    }
  });

  // Reset trail when animation resets
  useEffect(() => {
    if (animation.currentTime === 0) {
      trailPoints.current = [];
    }
  }, [animation.currentTime]);

  // Ball material with seam texture
  const ballMaterial = useMemo(() => {
    return new THREE.MeshPhongMaterial({
      color: '#dc2626',
      shininess: 30,
      specular: 0x111111
    });
  }, []);

  // Trail material
  const trailMaterial = useMemo(() => {
    return new THREE.LineBasicMaterial({
      color: '#fbbf24',
      opacity: 0.6,
      transparent: true,
      linewidth: 3
    });
  }, []);

  return (
    <group>
      {/* Cricket Ball */}
      <mesh
        ref={ballRef}
        position={ballPosition}
        castShadow
        receiveShadow
      >
        <sphereGeometry args={[0.0365]} /> {/* Cricket ball radius: 3.65cm */}
        <primitive object={ballMaterial} />
      </mesh>
      
      {/* Ball seam (visual detail) */}
      <mesh position={ballPosition}>
        <torusGeometry args={[0.0365, 0.002, 4, 16]} />
        <meshBasicMaterial color="#ffffff" />
      </mesh>
      
      {/* Ball trail */}
      {trailPoints.current.length > 1 && (
        <primitive object={new THREE.Line(new THREE.BufferGeometry(), trailMaterial)} ref={trailRef} />
      )}
      
      {/* Impact effect */}
      {animation.currentPhase === 'impact' && (
        <group position={ballPosition}>
          {/* Impact flash */}
          <mesh>
            <sphereGeometry args={[0.3]} />
            <meshBasicMaterial
              color="#ffffff"
              transparent
              opacity={0.8}
            />
          </mesh>
          
          {/* Impact particles */}
          {[...Array(8)].map((_, i) => (
            <mesh
              key={i}
              position={[
                Math.cos(i * Math.PI / 4) * 0.2,
                Math.sin(i * Math.PI / 4) * 0.1,
                Math.sin(i * Math.PI / 4) * 0.2
              ]}
            >
              <sphereGeometry args={[0.02]} />
              <meshBasicMaterial color="#fbbf24" transparent opacity={0.7} />
            </mesh>
          ))}
        </group>
      )}
      
      {/* Spin visualization */}
      {animation.isPlaying && animation.currentPhase === 'postImpact' && (
        <group position={ballPosition}>
          <mesh rotation={[0, 0, animation.currentTime * 0.01]}>
            <torusGeometry args={[0.08, 0.005, 8, 16]} />
            <meshBasicMaterial color="#3b82f6" transparent opacity={0.3} />
          </mesh>
        </group>
      )}
      
      {/* Landing prediction marker */}
      {animation.isPlaying && 
       animation.currentPhase === 'postImpact' && 
       trajectory.points.length > 0 && (
        <mesh
          position={[
            trajectory.points[trajectory.points.length - 1].position.x,
            0.05,
            trajectory.points[trajectory.points.length - 1].position.z
          ]}
        >
          <cylinderGeometry args={[0.5, 0.5, 0.1]} />
          <meshBasicMaterial color="#dc2626" transparent opacity={0.3} />
        </mesh>
      )}
    </group>
  );
};